import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/App";
import { HardHat, Eye, EyeOff, Loader2, ShieldCheck, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const LoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { user, login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate("/dashboard");
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const success = await login(email, password);
    if (success) {
      navigate("/dashboard");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-zinc-950 flex flex-col">
      {/* Hero Section */}
      <div 
        className="relative h-56 bg-cover bg-center"
        style={{ 
          backgroundImage: "url('https://images.unsplash.com/photo-1758876734777-dcc6981f3671?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHwyfHxjb25zdHJ1Y3Rpb24lMjBzaXRlJTIwaW5zcGVjdGlvbiUyMHNhZmV0eSUyMGhlbG1ldCUyMGJsdWVwcmludHxlbnwwfHx8fDE3NzI5MDA3ODd8MA&ixlib=rb-4.1.0&q=85')" 
        }}
      >
        <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/80 to-transparent"></div>
        <div className="absolute bottom-5 left-4 right-4">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <HardHat size={28} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white tracking-wide" data-testid="login-title">SUIVI BTP</h1>
              <p className="text-zinc-500 text-xs tracking-wide">by AVIS DOM PLUS</p>
            </div>
          </div>
          <p className="text-white text-base font-medium leading-snug" data-testid="login-tagline">
            Gérez vos chantiers simplement, depuis le terrain.
          </p>
          <p className="text-zinc-400 text-sm mt-1" data-testid="login-subtitle">
            Photos, observations et rapports professionnels en quelques clics.
          </p>
        </div>
      </div>

      {/* Login Form */}
      <div className="flex-1 px-6 pt-8 pb-12">
        <div className="max-w-sm mx-auto">
          <h2 className="text-xl font-bold text-white mb-6">Connexion</h2>
          
          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="email" className="text-zinc-300 text-sm uppercase tracking-wider">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="votre@email.fr"
                required
                className="input-dark w-full"
                data-testid="login-email"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-zinc-300 text-sm uppercase tracking-wider">
                Mot de passe
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="input-dark w-full pr-12"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-500 hover:text-zinc-300"
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="btn-primary w-full"
              data-testid="login-submit"
            >
              {loading ? (
                <Loader2 className="animate-spin" size={20} />
              ) : (
                "Se connecter"
              )}
            </Button>
          </form>

          <div className="flex items-center justify-center gap-2 mt-6" data-testid="login-security-notice">
            <ShieldCheck size={14} className="text-zinc-500" />
            <p className="text-zinc-500 text-xs">Connexion sécurisée — données protégées</p>
          </div>

          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-zinc-600" data-testid="login-legal-links">
            <Link to="/legal" className="hover:text-zinc-400 transition-colors flex items-center gap-1">
              <FileText size={11} /> Mentions légales
            </Link>
            <span className="text-zinc-800">|</span>
            <Link to="/privacy" className="hover:text-zinc-400 transition-colors flex items-center gap-1">
              <ShieldCheck size={11} /> Confidentialité
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
